import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  HeartIcon,
  ShoppingCartIcon,
  MinusIcon,
  PlusIcon,
  TruckIcon,
  ShieldCheckIcon,
  HomeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

// import { ProductProps } from '../../components/products/ProductCard';
import { useCart } from '../../context/CartContext';
import { productService } from '../../services/product.service';
import { showSuccess } from '../../utils/notificationUtils';
// import { v4 as uuidv4 } from 'uuid';
import { Product } from '../../types/product';
import RichTextContent from '../../components/common/RichTextContent';
import {
  getProductImageList,
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';

// Define interface for product variant
interface ProductVariant {
  _id: string;
  name: string;
  description?: string;
  images?: string[];
  status: string;
  pricing: {
    original_price: number;
    sale_price?: number;
  };
}

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab] = useState<'description' | 'nutrition' | 'reviews'>(
    'description'
  );
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Add state for selected variant
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    null
  );

  const { addItem } = useCart();

  // Format unit for display
  const formatUnit = (unit: string | undefined): string => {
    if (!unit) return '';

    const unitMap: Record<string, string> = {
      kg: 'Kg',
      gram: 'Gram',
      bunch: 'Bó',
      basket: 'Rổ',
      piece: 'Cái',
      box: 'Hộp',
      bottle: 'Chai',
      other: 'Khác',
    };

    return unitMap[unit] || unit;
  };

  // Check if the product is sold by weight (kg)
  const isSoldByWeight = (): boolean => {
    return product?.unit === 'kg';
  };

  // Check if product has multiple variants
  const hasVariants = (): boolean => {
    return (
      product?.type === 'multiple_variants' &&
      Array.isArray(product.variants) &&
      product.variants.length > 0
    );
  };

  // Get current price based on selected variant or product
  const getCurrentPrice = (): number => {
    // console.log('1: ', hasVariants())
    // console.log('1: ', selectedVariant)
    if (hasVariants() && selectedVariant) {
      // console.log('2 :', selectedVariant.pricing.sale_price ||
        // selectedVariant.pricing.original_price)
      return (
        selectedVariant.pricing.sale_price ||
        selectedVariant.pricing.original_price
      );
    }
    // console.log('3 :', product?.current_price)
    return product?.current_price ?? product?.sale_price ?? product?.original_price ?? 0;
  };

  // Get original price based on selected variant or product
  const getOriginalPrice = (): number => {
    if (hasVariants() && selectedVariant) {
      return selectedVariant.pricing.original_price;
    }
    return product?.original_price || 0;
  };

  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!slug) {
          throw new Error('Không tìm thấy sản phẩm');
        }

        // Reset state when product changes
        setSelectedImage(0);

        // Fetch product details
        const productData = await productService.getProductDetail(slug);
        if (!productData) {
          throw new Error('Không tìm thấy sản phẩm');
        }

        // Set initial quantity based on unit
        if (productData.unit === 'kg') {
          setQuantity(0.5); // Start with 0.5kg for weight-based products
        } else {
          setQuantity(1); // Start with 1 for count-based products
        }

        setProduct(productData);

        // Set initial selected variant if product has variants
        if (
          productData.type === 'multiple_variants' &&
          Array.isArray(productData.variants) &&
          productData.variants.length > 0
        ) {
          setSelectedVariant(productData.variants[0]);
        }

        // Fetch related products
        // const related = await getRelatedProducts(productData.id);
        // setRelatedProducts(related);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Đã có lỗi xảy ra');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [slug, navigate]);

  const incrementQuantity = () => {
    if (product && product.status === 'active') {
      if (isSoldByWeight()) {
        // Increment by 0.5 for kg
        setQuantity((prev) => parseFloat((prev + 0.5).toFixed(1)));
      } else {
        // Increment by 1 for other units
        setQuantity((prev) => prev + 1);
      }
    }
  };

  const decrementQuantity = () => {
    if (isSoldByWeight()) {
      // Don't go below 0.5 for kg
      if (quantity > 0.5) {
        setQuantity((prev) => parseFloat((prev - 0.5).toFixed(1)));
      }
    } else {
      // Don't go below 1 for other units
      if (quantity > 1) {
        setQuantity((prev) => prev - 1);
      }
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);

    if (isNaN(value)) return;

    if (isSoldByWeight()) {
      // For kg: ensure it's a multiple of 0.5 and at least 0.5
      const roundedValue = Math.round(value * 2) / 2; // Round to nearest 0.5
      setQuantity(Math.max(0.5, roundedValue));
    } else {
      // For other units: ensure it's an integer and at least 1
      setQuantity(Math.max(1, Math.floor(value)));
    }
  };

  const handleVariantChange = (variantId: string) => {
    console.log('variantId', variantId);
    if (!product || !product.variants) return;

    const variant = product.variants.find((v) => v._id === variantId);
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  // Touch handlers for image slider
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && displayImages.length > 0 && selectedImage < displayImages.length - 1) {
      setSelectedImage(selectedImage + 1);
    }
    if (isRightSwipe && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      // Determine product type
      let productType = 'single'; // Default type

      if (product.type === 'multiple_variants') {
        productType = 'multiple_variants';
      } else if (product.type === 'fruit_box') {
        productType = 'fruit_box';
      }

      // Create product with simple price (no weight calculation)
      const cartProduct = {
        id:
          hasVariants() && selectedVariant
            ? `${product.id}_${selectedVariant._id}`
            : product.id,
        productId: product.id,
        name:
          product.name +
          (hasVariants() && selectedVariant
            ? ` - ${selectedVariant.name}`
            : ''),
        price: getCurrentPrice(),
        originalPrice: getOriginalPrice(),
        salePrice: product.sale_price,
        currentPrice: getCurrentPrice(),
        image: getProductImageUrl(product.images?.[0]),
        images: product.images || [], // Array with the main image
        quantity: quantity, // Initial quantity for cart
        weight: product.unit || '',
        unit: formatUnit(product.unit) || '',
        stock: 99, // Default stock if not available
        type: productType, // Set the product type
        selected: false,
        variantId:
          hasVariants() && selectedVariant ? selectedVariant._id : undefined,
        variantName:
          hasVariants() && selectedVariant ? selectedVariant.name : undefined,
      };

      console.log('cartProduct', cartProduct);

      addItem(cartProduct, quantity);
      // Show a success notification
      showSuccess(
        'Đã thêm vào giỏ hàng'
      );
    }
  };

  // Convert Product[] to ProductProps[] for use with ProductGrid
  // const mapToProductProps = (products: Product[]): [] => {
  //   return products.map((product) => ({
  //     id: product.id,
  //     name: product.name,
  //     description: product.description,
  //     main_image: product.main_image,
  //     unit: product.unit,
  //     original_price: product.original_price,
  //     is_on_sale: product.is_on_sale,
  //     is_trending: product.is_trending,
  //     created_at: product.created_at,
  //   }));
  // };

  if (loading) {
    return (
      <div className='container px-4 py-12 mx-auto'>
        <div className='flex items-center justify-center min-h-[300px]'>
          <div className='w-12 h-12 rounded-full border-b-2 animate-spin border-primary'></div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className='container px-4 py-12 mx-auto'>
        <div className='p-4 text-red-700 bg-red-50 rounded-md'>
          <p>{error || 'Không tìm thấy sản phẩm'}</p>
        </div>
      </div>
    );
  }

  const displayImages = getProductImageList(product.images);

  return (
    <div className='container mx-auto px-4 py-8 pb-24 md:pb-8 font-["Quicksand"]'>
      {/* Breadcrumb Navigation */}
      <nav className='flex items-center mb-6 text-base'>
        <RouterLink
          to='/'
          className='flex items-center font-medium text-primary hover:text-primary-dark'
        >
          <HomeIcon className='h-5 w-5 mr-1.5' />
          <span>Trang chủ</span>
        </RouterLink>
        <ChevronRightIcon className='mx-3 w-5 h-5 text-gray-400' />
        <span className='font-medium text-gray-800 truncate'>
          {product.name}
        </span>
      </nav>

      {/* Mobile Sticky Cart Section */}
      <div className='md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 p-4'>
        <div className='flex items-center justify-between'>
          {/* Price Display */}
          <div className='flex-1'>
            <div className='text-lg font-bold text-primary'>
              {(quantity * getCurrentPrice()).toLocaleString('vi-VN')}₫
            </div>
            <div className='text-xs text-gray-500'>
              {quantity} {formatUnit(product.unit)}
            </div>
          </div>
          
          {/* Quantity Selector */}
          <div className='flex items-center space-x-2 mx-4'>
            <button
              type='button'
              onClick={decrementQuantity}
              disabled={product.status !== 'active'}
              className={`rounded-full w-8 h-8 flex items-center justify-center border ${
                product.status === 'active'
                  ? 'text-primary border-primary hover:bg-primary hover:text-white'
                  : 'text-gray-400 border-gray-300 cursor-not-allowed'
              } transition-colors`}
            >
              <MinusIcon className='w-3 h-3' />
            </button>
            
            <input
              type='number'
              value={quantity}
              onChange={handleQuantityChange}
              min={isSoldByWeight() ? '0.5' : '1'}
              step={isSoldByWeight() ? '0.5' : '1'}
              max={product.status === 'active' ? '10' : '0'}
              disabled={product.status !== 'active'}
              className='w-12 h-8 text-center rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm'
              style={{
                WebkitAppearance: 'none',
                MozAppearance: 'textfield',
              }}
            />
            
            <button
              type='button'
              onClick={incrementQuantity}
              disabled={product.status !== 'active'}
              className={`rounded-full w-8 h-8 flex items-center justify-center border ${
                product.status === 'active'
                  ? 'text-primary border-primary hover:bg-primary hover:text-white'
                  : 'text-gray-400 border-gray-300 cursor-not-allowed'
              } transition-colors`}
            >
              <PlusIcon className='w-3 h-3' />
            </button>
          </div>
          
          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCart}
            className={`px-6 py-3 rounded-lg text-sm font-medium ${
              product.status === 'active'
                ? 'bg-primary text-white hover:bg-primary-dark'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            } transition duration-200`}
            disabled={product.status !== 'active'}
          >
            {product.status === 'active' ? 'Thêm' : 'Hết hàng'}
          </button>
        </div>
      </div>

      <div className='overflow-hidden mb-8 bg-white rounded-lg border border-gray-100 shadow-sm'>
        <div className='grid grid-cols-1 gap-8 p-6 md:grid-cols-5'>
          {/* Product Images */}
          <div className='md:col-span-2'>
            {/* Mobile Image Slider */}
            <div className='md:hidden relative'>
              <div 
                className='aspect-square rounded-lg overflow-hidden bg-gray-50 relative'
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  src={getProductImageUrl(product?.images?.[selectedImage])}
                  alt={product.name}
                  className='object-contain w-full h-full'
                  onError={handleProductImageError}
                />
                
                {/* Out of stock badge */}
                {product.status !== 'active' && (
                  <div className='absolute top-3 right-3 z-10'>
                    <div className='bg-red-500 text-white px-2 py-1 rounded-md text-xs font-medium'>
                      Hết hàng
                    </div>
                  </div>
                )}
                
                {/* Image counter */}
                {product?.images && product.images.length > 1 && (
                  <div className='absolute bottom-3 right-3 z-10'>
                    <div className='bg-black/50 text-white px-2 py-1 rounded-md text-xs'>
                      {selectedImage + 1}/{product.images.length}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Swipe indicators */}
              {product?.images && product.images.length > 1 && (
                <div className='flex justify-center mt-3 space-x-2'>
                  {product.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        selectedImage === index ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Desktop Image Display */}
            <div className='hidden md:block'>
              <div className='mb-4 aspect-square rounded-lg overflow-hidden bg-gray-50 p-6 max-w-[450px] mx-auto relative'>
                <img
                  src={getProductImageUrl(product?.images?.[selectedImage])}
                  alt={product.name}
                  className='object-contain w-full h-full'
                  onError={handleProductImageError}
                />

                {/* Out of stock badge */}
                {product.status !== 'active' && (
                  <div className='absolute top-0 right-0 z-10 m-3'>
                    <div className='bg-red-500 text-white px-3 py-1.5 rounded-md text-sm font-medium'>
                      Hết hàng
                    </div>
                  </div>
                )}
              </div>
              <div className='grid grid-cols-5 gap-2'>
                {getProductImageList(product?.images).map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded-md overflow-hidden border-2 ${
                      selectedImage === index
                        ? 'border-primary'
                        : 'border-transparent'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} - Hình ${index + 1}`}
                      className='object-cover w-full h-full'
                      onError={handleProductImageError}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className='md:col-span-3'>
            <div className='mb-2 text-sm text-primary'>
              {/* {product.category.name} */}
            </div>
            <h1 className='mb-2 text-xl font-bold sm:text-2xl'>
              {product.name}
              {hasVariants() && selectedVariant && (
                <span className='ml-2 text-lg font-medium text-gray-600'>
                  - {selectedVariant.name}
                </span>
              )}
            </h1>

            {/* Product status badge */}
            {product.status !== 'active' && (
              <div className='inline-block bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full mb-3'>
                Hết hàng
              </div>
            )}

            <div className='flex items-baseline mb-4'>
              <div
                className={`text-2xl font-bold ${
                  product.status !== 'active' ? 'text-gray-500' : 'text-primary'
                }`}
              >
                {getCurrentPrice().toLocaleString('vi-VN')}₫
              </div>
              <div className='ml-2 text-sm text-gray-500'>
                /{formatUnit(product.unit)}
              </div>
            </div>

            <hr className='my-4 border-gray-100' />


            {/* Variant Selection */}
            {hasVariants() && (
              <div className='mb-4'>
                <label className='block mb-2 text-sm font-medium text-gray-700'>
                  Chọn loại:
                </label>
                <div className='flex flex-wrap gap-2'>
                  {product.variants?.map((variant) => (
                    <button
                      key={variant._id}
                      onClick={() => handleVariantChange(variant._id)}
                      className={`px-3 py-2 border rounded-full text-xs font-medium transition-all duration-200 ${
                        selectedVariant?._id === variant._id
                          ? 'border-primary bg-primary text-white shadow-md'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-primary hover:bg-primary/5'
                      } ${
                        variant.status !== 'active'
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      disabled={variant.status !== 'active'}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>{variant.name}</span>
                        <span className={`text-xs ${
                          selectedVariant?._id === variant._id 
                            ? 'text-white/80' 
                            : 'text-primary'
                        }`}>
                          {variant.pricing.original_price.toLocaleString('vi-VN')}₫
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selection - Hidden on Mobile */}
            <div className='mb-5 hidden md:block'>
              <label className='block mb-2 font-semibold text-gray-700'>
                Số lượng:
              </label>
              <div className='flex items-center space-x-3'>
                {/* Minus button */}
                <button
                  type='button'
                  onClick={decrementQuantity}
                  disabled={product.status !== 'active'}
                  className={`rounded-full w-9 h-9 flex items-center justify-center border ${
                    product.status === 'active'
                      ? 'text-primary border-primary hover:bg-primary hover:text-white'
                      : 'text-gray-400 border-gray-300 cursor-not-allowed'
                  } transition-colors`}
                >
                  <MinusIcon className='w-4 h-4' />
                </button>

                {/* Quantity input */}
                <input
                  type='number'
                  value={quantity}
                  onChange={handleQuantityChange}
                  min={isSoldByWeight() ? '0.5' : '1'}
                  step={isSoldByWeight() ? '0.5' : '1'}
                  max={product.status === 'active' ? '10' : '0'}
                  disabled={product.status !== 'active'}
                  className='w-16 h-10 text-center rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                  }}
                />

                {/* Plus button */}
                <button
                  type='button'
                  onClick={incrementQuantity}
                  disabled={product.status !== 'active'}
                  className={`rounded-full w-9 h-9 flex items-center justify-center border ${
                    product.status === 'active'
                      ? 'text-primary border-primary hover:bg-primary hover:text-white'
                      : 'text-gray-400 border-gray-300 cursor-not-allowed'
                  } transition-colors`}
                >
                  <PlusIcon className='w-4 h-4' />
                </button>
              </div>
              {isSoldByWeight() && (
                <div className='mt-1 text-xs text-gray-500'>
                  Bạn có thể chọn số lượng theo mức 0.5kg
                </div>
              )}
            </div>

            {/* Price summary */}
            <div className='p-4 mb-5 bg-gray-50 rounded-lg border border-gray-200'>
              <div className='flex justify-between items-center mb-1'>
                <span className='text-gray-700'>Đơn giá:</span>
                <span className='font-medium'>
                  {getCurrentPrice().toLocaleString('vi-VN')}₫/
                  {formatUnit(product.unit)}
                </span>
              </div>
              <div className='flex justify-between items-center mb-1'>
                <span className='text-gray-700'>Số lượng:</span>
                <span className='font-medium'>
                  {quantity} {formatUnit(product.unit)}
                </span>
              </div>
              <div className='flex justify-between items-center pt-2 mt-2 border-t border-gray-200'>
                <span className='font-semibold text-gray-700'>Tổng tiền:</span>
                <span className='text-lg font-bold text-primary'>
                  {(quantity * getCurrentPrice()).toLocaleString('vi-VN')}₫
                </span>
              </div>
            </div>

            {/* Add to Cart - Hidden on Mobile */}
            <div className='mt-6 mb-6 space-y-4 hidden md:block'>
              <div className='flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4'>
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg ${
                    product.status === 'active'
                      ? 'bg-primary text-white hover:bg-primary-dark'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  } transition duration-200`}
                  disabled={product.status !== 'active'}
                >
                  <ShoppingCartIcon className='mr-2 w-5 h-5' />
                  {product.status === 'active'
                    ? 'Thêm vào giỏ hàng'
                    : 'Hết hàng'}
                </button>

                <button
                  onClick={toggleFavorite}
                  className='hidden justify-center items-center p-3 text-gray-600 rounded-lg border border-gray-300 sm:flex hover:border-red-500 hover:bg-red-50'
                >
                  {isFavorite ? (
                    <HeartIconSolid className='w-5 h-5 text-red-500' />
                  ) : (
                    <HeartIcon className='w-5 h-5' />
                  )}
                </button>
              </div>
            </div>

            {/* Shipping & Guarantee */}
            <div
              className='p-4 rounded-lg border'
              style={{
                backgroundColor: 'rgba(229, 62, 62, 0.05)',
                borderColor: 'rgba(229, 62, 62, 0.2)',
              }}
            >
              <div className='flex items-start mb-3'>
                <TruckIcon className='h-5 w-5 text-primary mt-0.5 mr-2' />
                <div>
                  <p className='font-medium text-gray-800'>
                    Giao hàng nhanh chóng
                  </p>
                  <p className='text-sm text-gray-600'>
                    Giao hoả tốc nội thành Hà Nội
                  </p>
                </div>
              </div>
              <div className='flex items-start'>
                <ShieldCheckIcon className='h-5 w-5 text-primary mt-0.5 mr-2' />
                <div>
                  <p className='font-medium text-gray-800'>
                    Cam kết chất lượng
                  </p>
                  <p className='text-sm text-gray-600'>
                    Trái cây tươi ngon, có chế độ bảo hành
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Tabs */}
        {product.description && (
          <div className='p-6 border-t border-gray-200'>
            <h3 className='mb-4 text-lg font-semibold text-gray-800'>
              Mô tả sản phẩm
            </h3>
            <RichTextContent content={product.description} />

            {/* Show variant description if selected */}
            {hasVariants() &&
              selectedVariant &&
              selectedVariant.description && (
                <div className='pt-4 mt-4 border-t border-gray-100'>
                  <h4 className='mb-2 font-medium'>{selectedVariant.name}</h4>
                  <p className='text-gray-700'>{selectedVariant.description}</p>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
